/*
  <fd-product-order> – variant selection, quantity and add to cart for one product.
  Markup: snippets/fd-product-order.liquid. Depends on assets/fd-core.js (window.FD).

  Why this replaces the old variants.js / quick-shop.js:
  - Variants are matched client-side from one JSON block, so changing an option never
    needs a network round trip and can never leave the button in a stale state.
  - Choosing an option value that does not exist with the current other options picks the
    closest real variant instead of dead-ending; combinations that don't exist or are sold
    out are marked in the UI (they remain selectable).
  - Nothing is ever added to the cart without an explicit click on the add button.
  - Quantity lives inside the same <form>; stock limits are applied to the stepper.
  - Errors from Shopify (e.g. stock) are shown inline, in a live region.

  Events
    fd:variant:change   (bubbles from the element) detail { productId, variant }
    fd:cart:updated     (from FD.cart.add) detail { action: 'add', source: 'order' }
*/
(function () {
  'use strict';
  if (!window.FD || customElements.get('fd-product-order')) return;
  var FD = window.FD;

  function toInt(v, d) { v = parseInt(v, 10); return isNaN(v) ? d : v; }

  class FdProductOrder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.context = this.dataset.context || 'card';
      this.isPage = this.context === 'page';
      this.pack = toInt(this.dataset.pack, 0);
      this.productId = this.dataset.productId;
      this.title = this.dataset.title || '';
      this.form = this.querySelector('form');
      if (!this.form) return; // sold-out product renders no form

      this.idInput = this.form.querySelector('[data-fd-variant-id]');
      this.qtyEl = this.form.querySelector('fd-qty');
      this.button = this.form.querySelector('[data-fd-submit]');
      this.buttonLabel = this.form.querySelector('[data-fd-submit-label]');
      this.errorEl = this.form.querySelector('[data-fd-error]');
      this.availabilityEl = this.form.querySelector('[data-fd-availability]');
      this.labelAdd = this.dataset.labelAdd || FD.t('add_to_cart');
      this.labelSold = this.dataset.labelSold || FD.t('sold_out');
      this.busy = false;

      var json = this.querySelector('script[data-fd-variants]');
      this.variants = [];
      if (json) {
        try { this.variants = JSON.parse(json.textContent); } catch (e) { this.variants = []; }
      }
      if (!this.variants.length && this.idInput) {
        // Never leave the button dead if the data block is missing: submit the rendered variant.
        this.variants = [{
          id: toInt(this.idInput.value, 0), options: [], title: '', sku: '', price: 0, compare: 0,
          available: !(this.button && this.button.disabled), stock: null, media: 0
        }];
      }
      this.multi = this.variants.length > 1;
      this.optionEls = Array.prototype.slice.call(this.form.querySelectorAll('[data-fd-option]'));

      if (this.multi) {
        // From now on JS owns the variant id; the <noscript> select is only for no-JS.
        this.idInput.name = 'id';
        this.selected = this._readSelection();
        this.current = this._find(this.selected) || this.variants[0];
        this.form.addEventListener('change', this._onOptionChange.bind(this));
        this._refreshOptionStates();
      } else {
        this.current = this.variants[0] || null;
      }

      this.form.addEventListener('submit', this._onSubmit.bind(this));
      this.addEventListener('input', this._clearError.bind(this));

      if (this.isPage) this._initSticky();
      if (this.current) this._applyStock(this.current);
    }

    /* ---------- Variant matching ---------- */
    _readSelection() {
      var byIndex = [];
      this.optionEls.forEach(function (el) {
        var i = toInt(el.dataset.fdOption, -1);
        if (i < 0) return;
        if (el.type === 'radio') { if (el.checked) byIndex[i] = el.value; }
        else byIndex[i] = el.value;
      });
      // Options with a single value render no control – fill from the first variant.
      var base = this.variants[0] ? this.variants[0].options : [];
      var current = this.variants.filter(function (v) { return String(v.id) === String(this.idInput.value); }, this)[0];
      var fallback = current ? current.options : base;
      return fallback.map(function (val, i) { return byIndex[i] != null ? byIndex[i] : val; });
    }

    _find(selection) {
      return this.variants.filter(function (v) {
        return v.options.every(function (o, i) { return o === selection[i]; });
      })[0] || null;
    }

    // Best real variant after the user set option `index` to `value`.
    _pick(index, value) {
      var next = this.selected.slice();
      next[index] = value;
      var exact = this._find(next);
      if (exact) return exact;
      var best = null, bestScore = -1;
      this.variants.forEach(function (v) {
        if (v.options[index] !== value) return;
        var score = 0;
        v.options.forEach(function (o, i) {
          // earlier options are more important to keep
          if (i !== index && o === next[i]) score += 10 * (v.options.length - i);
        });
        if (v.available) score += 5;
        if (score > bestScore) { best = v; bestScore = score; }
      });
      return best;
    }

    _onOptionChange(event) {
      var el = event.target;
      if (!el.matches || !el.matches('[data-fd-option]')) return;
      event.stopPropagation();
      var variant = this._pick(toInt(el.dataset.fdOption, 0), el.value);
      if (!variant) return;
      this._clearError();
      this._select(variant);
    }

    _select(variant) {
      this.current = variant;
      this.selected = variant.options.slice();
      this.idInput.value = variant.id;

      // Reflect the (possibly auto-adjusted) selection in every control.
      this.optionEls.forEach(function (el) {
        var i = toInt(el.dataset.fdOption, -1);
        var val = this.selected[i];
        if (el.type === 'radio') el.checked = el.value === val;
        else if (el.value !== val) el.value = val;
        if (el.type === 'radio' && el.checked) {
          var out = this.querySelector('[data-fd-option-selected="' + i + '"]');
          if (out) out.textContent = val;
        }
      }, this);

      this._refreshOptionStates();
      this._renderPrice(variant);
      this._applyStock(variant);
      if (this.isPage) this._syncUrl(variant);

      FD.emit('fd:variant:change', { productId: this.productId, variant: variant }, this);
    }

    // Marks values that are sold out or do not exist with the other current choices.
    _refreshOptionStates() {
      var self = this;
      this.optionEls.forEach(function (el) {
        var i = toInt(el.dataset.fdOption, -1);
        if (el.tagName === 'SELECT') {
          Array.prototype.forEach.call(el.options, function (opt) {
            var state = self._valueState(i, opt.value);
            var base = opt.dataset.label || opt.value;
            opt.textContent = state === 'soldout' ? base + ' – ' + FD.t('sold_out')
              : state === 'unavailable' ? base + ' – ' + FD.t('option_unavailable') : base;
          });
        } else if (el.type === 'radio') {
          var pill = el.closest('.fd-pill');
          if (!pill) return;
          var st = self._valueState(i, el.value);
          pill.classList.toggle('is-soldout', st === 'soldout');
          pill.classList.toggle('is-unavailable', st === 'unavailable');
        }
      });
    }

    _valueState(index, value) {
      var sel = this.selected;
      var match = this.variants.filter(function (v) {
        return v.options[index] === value && v.options.every(function (o, j) { return j === index || o === sel[j]; });
      })[0];
      if (!match) return 'unavailable';
      return match.available ? 'ok' : 'soldout';
    }

    /* ---------- Rendering ---------- */
    _renderPrice(v) {
      var cur = this.querySelector('[data-fd-price-current]');
      var cmp = this.querySelector('[data-fd-price-compare]');
      var cmpVal = this.querySelector('[data-fd-price-compare-value]');
      var unit = this.querySelector('[data-fd-price-unit]');
      if (cur) cur.textContent = FD.money(v.price);
      if (cmp && cmpVal) {
        var onSale = v.compare > v.price;
        cmp.hidden = !onSale;
        if (onSale) cmpVal.textContent = FD.money(v.compare);
      }
      if (unit && this.pack > 0) unit.textContent = FD.unitPrice(v.price, this.pack) + ' ' + (unit.dataset.label || '');
      var sticky = this.querySelector('[data-fd-sticky-price]');
      if (sticky) sticky.textContent = FD.money(v.price);
    }

    _applyStock(v) {
      var available = !!v.available;
      if (this.button) {
        this.button.disabled = !available;
        this.button.removeAttribute('aria-disabled');
      }
      if (this.buttonLabel) this.buttonLabel.textContent = available ? this.labelAdd : this.labelSold;
      var stickyLabel = this.querySelector('[data-fd-sticky-label]');
      var stickyBtn = this.querySelector('[data-fd-sticky-submit]');
      if (stickyLabel) stickyLabel.textContent = available ? this.labelAdd : this.labelSold;
      if (stickyBtn) stickyBtn.disabled = !available;

      if (this.availabilityEl) {
        this.availabilityEl.textContent = available ? FD.t('in_stock') : FD.t('out_of_stock');
        this.availabilityEl.classList.toggle('fd-status--ok', available);
        this.availabilityEl.classList.toggle('fd-status--out', !available);
        // Cards stay quiet unless the chosen variant is sold out.
        if (!this.isPage) this.availabilityEl.hidden = available;
      }

      if (this.qtyEl && this.qtyEl.setMax) {
        this.qtyEl.setMax(v.stock == null ? null : Math.max(v.stock, 1));
        this.qtyEl.value = this.qtyEl.value; // re-clamp against the new limit
      }
    }

    _syncUrl(v) {
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      } catch (e) { /* non-critical */ }
    }

    /* ---------- Add to cart ---------- */
    _payload() {
      var data = new FormData(this.form);
      var id = data.get('id');
      var qty = this.qtyEl ? this.qtyEl.value : toInt(data.get('quantity'), 1);
      var item = { id: toInt(id, 0), quantity: qty };
      var plan = data.get('selling_plan');
      if (plan) item.selling_plan = plan;
      var props = {};
      data.forEach(function (value, name) {
        var m = /^properties\[(.+)\]$/.exec(name);
        if (m && String(value) !== '') props[m[1]] = String(value);
      });
      if (Object.keys(props).length) item.properties = props;
      return item;
    }

    _onSubmit(event) {
      event.preventDefault();
      if (this.busy || !this.current || !this.current.available) return;
      var item = this._payload();
      if (!item.id) return;
      var self = this;
      this._setBusy(true);
      this._clearError();

      FD.cart.add([item], 'order').then(function () {
        self._setBusy(false);
        FD.announce(FD.t('added', { title: self.title, count: item.quantity }));
        if (self.isPage) {
          FD.openCart();
        } else {
          self._flashAdded();
        }
        if (self.qtyEl) self.qtyEl.value = 1;
      }).catch(function (err) {
        self._setBusy(false);
        self._showError(err && err.message ? err.message : FD.t('error_generic'));
      });
    }

    _setBusy(on) {
      this.busy = on;
      if (!this.button) return;
      this.button.classList.toggle('is-loading', on);
      if (on) this.button.setAttribute('aria-disabled', 'true');
      else this.button.removeAttribute('aria-disabled');
    }

    _flashAdded() {
      if (!this.buttonLabel) return;
      var self = this;
      clearTimeout(this._flashTimer);
      this.buttonLabel.textContent = FD.t('added_short');
      this.button.classList.add('is-added');
      this._flashTimer = setTimeout(function () {
        self.buttonLabel.textContent = self.current && self.current.available ? self.labelAdd : self.labelSold;
        self.button.classList.remove('is-added');
      }, 2000);
    }

    _showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }
    _clearError() {
      if (this.errorEl && !this.errorEl.hidden) { this.errorEl.hidden = true; this.errorEl.textContent = ''; }
    }

    /* ---------- Sticky buy bar (product page, phones) ---------- */
    _initSticky() {
      var bar = this.querySelector('[data-fd-sticky]');
      var trigger = this.querySelector('[data-fd-sticky-submit]');
      if (!bar || !this.button || !('IntersectionObserver' in window)) return;
      var self = this;
      trigger.addEventListener('click', function () {
        if (self.button.disabled) return;
        if (typeof self.form.requestSubmit === 'function') self.form.requestSubmit(self.button);
        else self.button.click();
      });
      new IntersectionObserver(function (entries) {
        var e = entries[0];
        // Show only after the main button has scrolled out of view upwards.
        bar.hidden = e.isIntersecting || e.boundingClientRect.top > 0;
      }, { threshold: 0 }).observe(this.button);
    }
  }

  customElements.define('fd-product-order', FdProductOrder);
})();
